import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { TableLayout, type TableColumn } from "../../../components/dashboard";
import { BottomActionBar } from "../../../components/ui/BottomActionBar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { buildCsvFromRows, downloadCsvFile } from "../../../lib/csv";
import { usePurchaseOrders, type PurchaseOrderListRow } from "../hooks/usePurchaseOrders";
import { useDealers } from "../../dealers/hooks/useDealers";

const STATUSES = ["ALL", "DRAFT", "SENT", "CONFIRMED", "PARTIALLY_RECEIVED", "COMPLETED", "CANCELLED"] as const;

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function PurchaseOrderListPage() {
  const [status, setStatus] = useState<string>("ALL");
  const [dealerId, setDealerId] = useState<string>("ALL");
  const filters = useMemo(
    () => ({
      status: status === "ALL" ? undefined : status,
      dealerId: dealerId === "ALL" ? undefined : dealerId,
    }),
    [status, dealerId],
  );
  const { rows, loading, error, reload } = usePurchaseOrders(filters);
  const { dealers } = useDealers();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const selectedCount = selectedIds.size;
  const allFilteredSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(String(r.id)));
  const someFilteredSelected = rows.some((r) => selectedIds.has(String(r.id)));

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const r of rows) {
          if (checked) next.add(String(r.id));
          else next.delete(String(r.id));
        }
        return next;
      });
    },
    [rows],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const columns: TableColumn<PurchaseOrderListRow>[] = useMemo(
    () => [
      {
        key: "sel",
        header: "",
        headerRender: (
          <Checkbox
            checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
            onCheckedChange={(v) => toggleSelectAllFiltered(v === true)}
            aria-label="Select all visible purchase orders"
          />
        ),
        render: (r) => (
          <Checkbox
            checked={selectedIds.has(String(r.id))}
            onCheckedChange={(v) => toggleRow(String(r.id), v === true)}
            aria-label={`Select PO ${r.id}`}
          />
        ),
      },
      { key: "id", header: "PO ID", render: (r) => <span className="font-mono">{r.id}</span> },
      { key: "dealer", header: "Dealer", render: (r) => r.dealer.name },
      { key: "status", header: "Status", render: (r) => r.status },
      { key: "ordered", header: "Ordered", render: (r) => <span className="tabular-nums">{r.ordered_qty_total}</span> },
      { key: "received", header: "Received", render: (r) => <span className="tabular-nums">{r.received_qty_total}</span> },
      {
        key: "date",
        header: "Date",
        render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : "—"),
      },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <Button asChild variant="outline" size="sm">
            <Link to={`/purchase-orders/${r.id}`}>View</Link>
          </Button>
        ),
      },
    ],
    [
      allFilteredSelected,
      someFilteredSelected,
      toggleSelectAllFiltered,
      selectedIds,
      toggleRow,
    ],
  );

  const mobileCardRender = useCallback(
    (r: PurchaseOrderListRow) => (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2 min-w-0">
          <Checkbox
            checked={selectedIds.has(String(r.id))}
            onCheckedChange={(v) => toggleRow(String(r.id), v === true)}
            aria-label={`Select PO ${r.id}`}
          />
          <div className="min-w-0 flex-1 text-right">
            <div className="font-mono text-sm font-medium">#{r.id}</div>
            <div className="text-xs text-muted-foreground truncate">{r.dealer.name}</div>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0 min-h-9">
            <Link to={`/purchase-orders/${r.id}`}>View</Link>
          </Button>
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Status</dt>
          <dd className="text-right font-medium">{r.status}</dd>
          <dt className="text-muted-foreground">Ordered</dt>
          <dd className="text-right tabular-nums">{r.ordered_qty_total}</dd>
          <dt className="text-muted-foreground">Received</dt>
          <dd className="text-right tabular-nums">{r.received_qty_total}</dd>
          <dt className="text-muted-foreground">Date</dt>
          <dd className="text-right text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</dd>
        </dl>
      </div>
    ),
    [selectedIds, toggleRow],
  );

  const rowsToCsv = useCallback((list: typeof rows) => {
    const header = [
      "po_id",
      "dealer_id",
      "dealer_name",
      "status",
      "ordered_qty_total",
      "received_qty_total",
      "expected_delivery_date",
      "created_at",
    ];
    const data = list.map((r) => [
      String(r.id),
      String(r.dealer.id),
      r.dealer.name,
      r.status,
      r.ordered_qty_total,
      r.received_qty_total,
      r.expected_delivery_date ?? "",
      r.created_at ?? "",
    ]);
    return buildCsvFromRows(header, data);
  }, []);

  const exportAll = () => {
    if (rows.length === 0) return;
    downloadCsvFile(`purchase-orders-export-all-${stamp()}.csv`, rowsToCsv(rows));
  };

  const exportSelected = () => {
    const list = rows.filter((r) => selectedIds.has(String(r.id)));
    if (list.length === 0) return;
    downloadCsvFile(`purchase-orders-export-selected-${stamp()}.csv`, rowsToCsv(list));
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Purchase orders</h1>
          <p className="text-muted-foreground">Create, send, confirm, and receive against dealers.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={exportAll} disabled={loading || rows.length === 0}>
            Export all
          </Button>
          <Button asChild>
            <Link to="/purchase-orders/new">Create PO</Link>
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All purchase orders</CardTitle>
          <CardDescription>Filter by status and dealer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="min-w-0 flex-1 space-y-2 sm:min-w-[160px] sm:flex-initial">
              <div className="text-sm font-medium">Status</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="min-h-9 w-full">
                  <SelectValue placeholder="Any status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === "ALL" ? "Any" : s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 flex-1 space-y-2 sm:min-w-[180px] sm:flex-initial">
              <div className="text-sm font-medium">Dealer</div>
              <Select value={dealerId} onValueChange={setDealerId}>
                <SelectTrigger className="min-h-9 w-full">
                  <SelectValue placeholder="Any dealer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Any dealer</SelectItem>
                  {dealers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={() => void reload()}>
              Refresh
            </Button>
          </div>

          {error ? <div className="text-sm text-destructive">{error}</div> : null}

          {selectedCount > 0 ? (
            <div className="hidden flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 md:flex">
              <span className="text-sm font-medium">{selectedCount} selected</span>
              <Button type="button" size="sm" variant="outline" onClick={clearSelection}>
                Clear selection
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={exportSelected}>
                Export selected
              </Button>
            </div>
          ) : null}

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No purchase orders yet.</div>
          ) : (
            <div className="rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} mobileCardRender={mobileCardRender} />
            </div>
          )}
        </CardContent>
      </Card>

      {selectedCount > 0 ? (
        <BottomActionBar>
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <Button type="button" size="sm" variant="outline" onClick={clearSelection}>
            Clear
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={exportSelected}>
            Export
          </Button>
        </BottomActionBar>
      ) : null}
    </div>
  );
}
